/**
 * Panel de minijuego: la pieza que el alumno ve y manipula.
 *
 * Siempre se abre centrado y por encima de todo lo demás (`position: fixed`
 * con flex), sin depender del contenedor que lo aloje. Toda la lógica de qué
 * es correcto vive en `src/minigames/`; esta capa sólo pinta, recoge la
 * respuesta y traduce el resultado en retroalimentación.
 */

import type { MinigameSpec, TimerPolicy } from '../types/game';
import {
  buildItems,
  checkResponse,
  failureText,
  solutionText,
  type ItemResponse,
  type RuntimeItem,
  type WidgetModel
} from '../minigames/build';
import { normalizeText } from '../minigames/check';
import { formatNumber } from '../minigames/items';
import { button, clear, el, on, type El } from './dom';

export interface MinigameOutcome {
  /** Todos los ítems resueltos. */
  success: boolean;
  /** El jugador cerró el panel sin terminar. */
  abandoned: boolean;
  aciertos: number;
  errores: number;
  segundos: number;
  /** Pistas usadas en todo el panel; no descuentan puntuación. */
  pistas?: number;
}

export interface MinigameViewCallbacks {
  onClose: (outcome: MinigameOutcome) => void;
  onSfx?: (name: string) => void;
}

/** Controlador de un widget: pinta, lee la respuesta y se bloquea al acertar. */
interface Widget {
  node: El<'div'>;
  read(): ItemResponse | null;
  lock(): void;
  destroy(): void;
}

const OPTION_KEYS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const DEFAULT_INTENTOS = 3;
const DEFAULT_STRICT_SECONDS = 60;
const LINK_COLORS = 6;

type Mode = 'answer' | 'solved' | 'revealed';

export class MinigameView {
  private readonly items: RuntimeItem[];
  private readonly policy: TimerPolicy;
  private readonly maxIntentos: number;
  private readonly limite: number;

  private root: El<'div'> | null = null;
  private panelEl: El<'div'> | null = null;
  private metaEl!: El<'span'>;
  private timerEl!: El<'span'>;
  private questionEl!: El<'div'>;
  private expressionEl!: El<'div'>;
  private widgetHost!: El<'div'>;
  private feedbackEl!: El<'div'>;
  private actionsEl!: El<'div'>;

  private widget: Widget | null = null;
  private index = 0;
  private attempts = 0;
  private hintsUsed = 0;
  private pistasTotales = 0;
  private aciertos = 0;
  private errores = 0;
  private mode: Mode = 'answer';
  private startedAt = 0;
  private elapsed = 0;
  private timerId: number | null = null;
  private offEscape: (() => void) | null = null;
  private closed = false;

  constructor(
    private readonly host: HTMLElement,
    private readonly spec: MinigameSpec,
    private readonly callbacks: MinigameViewCallbacks
  ) {
    this.items = buildItems(spec);
    this.policy = spec.cronometro ?? 'suave';
    this.maxIntentos = Math.max(1, spec.intentos ?? DEFAULT_INTENTOS);
    this.limite = this.spec.segundos ?? (this.policy === 'estricto' ? DEFAULT_STRICT_SECONDS : 0);
  }

  open(): void {
    if (this.root) return;
    this.startedAt = Date.now();
    this.root = el('div', {
      className: 'ae-mg-overlay',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': this.spec.titulo ?? 'Minijuego' }
    });
    const panel = el('div', { className: 'ae-mg-panel' });
    this.panelEl = panel;
    panel.append(this.buildHeader(), this.buildMeta(), this.buildBody(), this.buildFeedback(), this.buildActions());
    this.root.append(panel);
    this.host.append(this.root);
    this.offEscape = on(document, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.finish(false, true);
    });
    this.startTimer();
    this.renderItem();
  }

  destroy(): void {
    this.stopTimer();
    this.offEscape?.();
    this.offEscape = null;
    this.widget?.destroy();
    this.widget = null;
    this.root?.remove();
    this.root = null;
    this.panelEl = null;
  }

  /* ------------------------------- estructura ------------------------------- */

  private buildHeader(): El<'div'> {
    return el('div', { className: 'ae-mg-header' }, [
      el('h2', { className: 'ae-mg-title', textContent: this.spec.titulo ?? 'Reto' }),
      button('✕', () => this.finish(false, true), { className: 'ae-mg-close', ariaLabel: 'Cerrar' })
    ]);
  }

  private buildMeta(): El<'div'> {
    const meta = el('div', { className: 'ae-mg-meta' });
    this.metaEl = el('span', { className: 'ae-mg-progress' });
    this.timerEl = el('span', { className: 'ae-mg-timer' });
    meta.append(this.metaEl, this.timerEl);
    return meta;
  }

  private buildBody(): El<'div'> {
    const body = el('div', { className: 'ae-mg-body' });
    this.questionEl = el('div', { className: 'ae-mg-question' });
    this.expressionEl = el('div', { className: 'ae-mg-expression' });
    this.widgetHost = el('div', { className: 'ae-mg-widget' });
    body.append(this.questionEl, this.expressionEl, this.widgetHost);
    return body;
  }

  private buildFeedback(): El<'div'> {
    this.feedbackEl = el('div', { className: 'ae-mg-feedback', attrs: { 'aria-live': 'polite' } });
    return this.feedbackEl;
  }

  private buildActions(): El<'div'> {
    this.actionsEl = el('div', { className: 'ae-mg-actions' });
    return this.actionsEl;
  }

  /* --------------------------------- flujo --------------------------------- */

  private renderItem(): void {
    const item = this.items[this.index];
    this.attempts = 0;
    this.hintsUsed = 0;
    this.mode = 'answer';

    this.questionEl.textContent = item.pregunta;
    this.expressionEl.textContent = item.expresion ?? '';
    this.expressionEl.hidden = !item.expresion;

    this.widget?.destroy();
    clear(this.widgetHost);
    this.widget = this.createWidget(item.widget);
    this.widgetHost.append(this.widget.node);

    this.paintMeta();
    this.setFeedback('', 'info');
    this.paintActions();
  }

  private paintMeta(): void {
    const total = this.items.length;
    const partes = [`Reto ${this.index + 1} de ${total}`];
    if (this.aciertos > 0) partes.push(`✓ ${this.aciertos}`);
    if (this.mode === 'answer') partes.push(`Intento ${Math.min(this.attempts + 1, this.maxIntentos)} de ${this.maxIntentos}`);
    this.metaEl.textContent = partes.join('  ·  ');
  }

  private paintTimer(): void {
    if (this.policy === 'ninguno') {
      this.timerEl.textContent = '';
      return;
    }
    if (this.policy === 'estricto') {
      const seconds = Math.max(0, Math.ceil(this.limite - this.elapsed));
      this.timerEl.textContent = `⏳ ${seconds}s`;
      this.timerEl.classList.toggle('ae-mg-time-critical', seconds <= 10);
      return;
    }
    const seconds = Math.floor(this.elapsed);
    this.timerEl.textContent = `⏱ ${seconds}s`;
    this.timerEl.classList.toggle('ae-mg-time-critical', this.limite > 0 && seconds > this.limite);
  }

  private paintActions(): void {
    clear(this.actionsEl);

    if (this.mode === 'answer') {
      this.actionsEl.append(button('Comprobar', () => this.submit(), { className: 'ae-btn-primary ae-mg-check' }));
      const hints = this.spec.hints ?? [];
      if (this.hintsUsed < hints.length) {
        this.actionsEl.append(button('Pista', () => this.showHint(), { className: 'ae-btn-hint' }));
      }
      return;
    }

    if (this.mode === 'solved') {
      const label = this.index + 1 < this.items.length ? 'Siguiente' : 'Terminar';
      this.actionsEl.append(button(label, () => this.advance(), { className: 'ae-btn-primary' }));
      return;
    }

    if (this.spec.reintentable !== false) {
      this.actionsEl.append(button('Intentar de nuevo', () => this.renderItem(), { className: 'ae-btn-primary' }));
    }
    this.actionsEl.append(
      button('Salir', () => this.finish(false, false), { className: 'ae-btn-back' })
    );
  }

  private submit(): void {
    if (this.mode !== 'answer' || !this.widget) return;
    const response = this.widget.read();
    if (response === null) {
      this.setFeedback('Termina de colocar tu respuesta.', 'hint');
      this.sfx('pop');
      return;
    }

    const item = this.items[this.index];
    const result = checkResponse(item, response);

    if (result.correct) {
      this.aciertos++;
      this.mode = 'solved';
      this.widget.lock();
      this.sfx('exito');
      this.celebrate();
      this.setFeedback(this.feedbackOnSuccess(item), 'success');
      this.paintMeta();
      this.paintActions();
      return;
    }

    this.errores++;
    this.attempts++;
    this.sfx('error');
    this.shake();

    if (this.attempts >= this.maxIntentos) {
      this.mode = 'revealed';
      this.widget.lock();
      const detalle = failureText(item, result);
      this.setFeedback(
        `${detalle}${detalle ? ' ' : ''}La solución es: ${solutionText(item)}.`,
        'warning'
      );
    } else {
      this.setFeedback(failureText(item, result) || 'Casi. Revísalo y vuelve a intentarlo.', 'warning');
    }
    this.paintMeta();
    this.paintActions();
  }

  private feedbackOnSuccess(item: RuntimeItem): string {
    return item.widget.explicacion || this.spec.explicacion || '¡Correcto!';
  }

  private static reducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Lluvia breve de confeti + pop del panel al acertar. */
  private celebrate(): void {
    if (!this.root || MinigameView.reducedMotion()) return;
    const layer = el('div', { className: 'ae-mg-confetti' });
    const colores = ['#ffd479', '#7ee08a', '#6fc3ff', '#ff8fb1', '#c99bff'];
    for (let i = 0; i < 26; i++) {
      const p = el('i');
      p.style.left = `${Math.random() * 100}%`;
      p.style.background = colores[i % colores.length];
      p.style.animationDelay = `${Math.random() * 0.25}s`;
      p.style.transform = `rotate(${Math.random() * 360}deg)`;
      layer.append(p);
    }
    this.root.append(layer);
    window.setTimeout(() => layer.remove(), 1400);
    this.popPanel('ae-mg-pop');
  }

  /** Sacudida + destello rojo al fallar, para que el error también se sienta. */
  private shake(): void {
    if (MinigameView.reducedMotion()) return;
    this.popPanel('ae-mg-bad');
  }

  private popPanel(className: string): void {
    const panel = this.panelEl;
    if (!panel) return;
    panel.classList.remove(className);
    // Reinicia la animación aunque se encadenen aciertos/fallos seguidos.
    void panel.offsetWidth;
    panel.classList.add(className);
    window.setTimeout(() => panel.classList.remove(className), 700);
  }

  private showHint(): void {
    const hints = this.spec.hints ?? [];
    const hint = hints[this.hintsUsed];
    if (!hint) return;
    this.hintsUsed++;
    this.pistasTotales++;
    this.setFeedback(`💡 ${hint}`, 'hint');
    this.sfx('magia');
    this.paintActions();
  }

  private advance(): void {
    if (this.index + 1 < this.items.length) {
      this.index++;
      this.renderItem();
      return;
    }
    this.sfx('aplauso');
    this.finish(true, false);
  }

  private finish(success: boolean, abandoned: boolean): void {
    if (this.closed) return;
    this.closed = true;
    const segundos = Math.round((Date.now() - this.startedAt) / 1000);
    this.destroy();
    this.callbacks.onClose({
      success,
      abandoned,
      aciertos: this.aciertos,
      errores: this.errores,
      segundos,
      pistas: this.pistasTotales
    });
  }

  /* -------------------------------- cronómetro ------------------------------- */

  private startTimer(): void {
    if (this.policy === 'ninguno') {
      this.paintTimer();
      return;
    }
    this.paintTimer();
    this.timerId = window.setInterval(() => this.tick(), 250);
  }

  private tick(): void {
    this.elapsed = (Date.now() - this.startedAt) / 1000;
    this.paintTimer();
    if (this.policy !== 'estricto') return;
    if (this.elapsed >= this.limite) {
      this.stopTimer();
      this.sfx('error');
      this.mode = 'revealed';
      this.widget?.lock();
      this.setFeedback(
        `Se acabó el tiempo. La solución es: ${solutionText(this.items[this.index])}.`,
        'warning'
      );
      this.paintActions();
    }
  }

  private stopTimer(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /* -------------------------------- feedback -------------------------------- */

  private setFeedback(text: string, tone: 'info' | 'success' | 'warning' | 'hint'): void {
    this.feedbackEl.textContent = text;
    this.feedbackEl.hidden = text === '';
    this.feedbackEl.className = `ae-mg-feedback ae-mg-tone-${tone}`;
  }

  private sfx(name: string): void {
    this.callbacks.onSfx?.(name);
  }

  /* --------------------------------- widgets -------------------------------- */

  private createWidget(model: WidgetModel): Widget {
    switch (model.kind) {
      case 'choice':
        return this.choiceWidget(model);
      case 'numeric':
        return this.numericWidget();
      case 'text':
        return this.textWidget(model);
      case 'ordering':
        return this.orderingWidget(model, this.spec.kind === 'recta-numerica');
      case 'matching':
        return this.matchingWidget(model);
      case 'sorting':
        return this.sortingWidget(model);
      case 'building':
        return this.buildingWidget(model);
      case 'wordsearch':
        return this.wordsearchWidget(model);
    }
  }

  private choiceWidget(model: Extract<WidgetModel, { kind: 'choice' }>): Widget {
    let selected = -1;
    let locked = false;
    const node = el('div', { className: 'ae-mg-choice' });
    const buttons: HTMLButtonElement[] = [];

    const repaint = (): void => {
      buttons.forEach((b, i) => b.classList.toggle('ae-selected', i === selected));
    };

    model.opciones.forEach((label, i) => {
      const b = el('button', { className: 'ae-mg-option', type: 'button' }, [
        el('span', { className: 'ae-mg-option-key', textContent: OPTION_KEYS[i] ?? String(i + 1) }),
        el('span', { className: 'ae-mg-option-text', textContent: label })
      ]);
      b.addEventListener('click', () => {
        if (locked) return;
        selected = i;
        repaint();
        this.sfx('pop');
      });
      buttons.push(b);
      node.append(b);
    });

    return {
      node,
      read: () => (selected < 0 ? null : { kind: 'choice', index: selected }),
      lock: () => {
        locked = true;
        buttons.forEach((b, i) => {
          b.disabled = true;
          b.classList.toggle('ae-correct', i === model.correcta);
        });
      },
      destroy: () => undefined
    };
  }

  private numericWidget(): Widget {
    let digits = '';
    let locked = false;
    const node = el('div', { className: 'ae-mg-numeric' });
    const display = el('div', { className: 'ae-mg-display', textContent: '—' });
    const keypad = el('div', { className: 'ae-mg-keypad' });

    const repaint = (): void => {
      display.textContent = digits === '' || digits === '-' ? '—' : formatNumber(Number(digits));
      display.classList.toggle('ae-negative', digits.startsWith('-'));
    };

    const press = (key: string): void => {
      if (locked) return;
      if (key === '⌫') digits = digits.slice(0, -1);
      else if (key === '±') digits = digits.startsWith('-') ? digits.slice(1) : `-${digits}`;
      else if (digits.replace('-', '').length < 9) digits += key;
      repaint();
      this.sfx('tecleo');
    };

    for (const key of ['1', '2', '3', '4', '5', '6', '7', '8', '9', '±', '0', '⌫']) {
      keypad.append(button(key, () => press(key), { className: 'ae-mg-key' }));
    }

    const offKey = on(document, 'keydown', (event: KeyboardEvent) => {
      if (locked || !this.root) return;
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        press(event.key);
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        press('⌫');
        return;
      }
      if (event.key === '-') {
        event.preventDefault();
        press('±');
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submit();
      }
    });

    node.append(display, keypad);
    repaint();

    return {
      node,
      read: () => {
        if (digits === '' || digits === '-') return null;
        const value = Number(digits);
        return Number.isFinite(value) ? { kind: 'numeric', value } : null;
      },
      lock: () => {
        locked = true;
      },
      destroy: offKey
    };
  }

  private textWidget(model: Extract<WidgetModel, { kind: 'text' }>): Widget {
    const node = el('div', { className: 'ae-mg-text' });
    const inputs: HTMLInputElement[] = [];

    model.blancos.forEach((_, i) => {
      const field = el('div', { className: 'ae-mg-text-field' });
      field.append(el('label', {
        className: 'ae-mg-text-label',
        textContent: model.blancos.length > 1 ? `Espacio ${i + 1}` : 'Tu respuesta'
      }));
      const input = el('input', {
        className: 'ae-mg-text-input',
        type: 'text',
        attrs: { autocomplete: 'off', 'aria-label': `Respuesta del espacio ${i + 1}` }
      });
      input.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.submit();
        }
      });
      inputs.push(input);
      field.append(input);
      node.append(field);
    });

    inputs[0]?.focus();

    return {
      node,
      read: () => {
        const values = inputs.map((input) => input.value.trim());
        return values.every((v) => v === '') ? null : { kind: 'text', values };
      },
      lock: () => {
        inputs.forEach((input) => (input.disabled = true));
      },
      destroy: () => undefined
    };
  }

  private orderingWidget(model: Extract<WidgetModel, { kind: 'ordering' }>, asLine: boolean): Widget {
    let placed: string[] = [];
    let locked = false;
    const node = el('div', { className: asLine ? 'ae-mg-order ae-mg-order-line' : 'ae-mg-order' });
    const slots = el('div', { className: 'ae-mg-slots' });
    const pool = el('div', { className: 'ae-mg-pool' });

    if (asLine && model.rango) {
      node.append(
        el('div', { className: 'ae-mg-line-axis' }, [
          el('span', { textContent: formatNumber(model.rango[0]) }),
          el('span', { className: 'ae-mg-line-track' }),
          el('span', { textContent: formatNumber(model.rango[1]) })
        ])
      );
    }

    const repaint = (): void => {
      clear(slots);
      model.orden.forEach((_, position) => {
        const pieceId = placed[position];
        const piece = pieceId ? model.piezas.find((p) => p.id === pieceId) : undefined;
        const slot = el('div', {
          className: piece ? 'ae-mg-slot ae-filled' : 'ae-mg-slot',
          textContent: piece ? piece.label : String(position + 1)
        });
        if (piece && !locked) {
          slot.addEventListener('click', () => {
            placed = placed.filter((_, i) => i !== position);
            repaint();
          });
          slot.classList.add('ae-clickable');
        }
        slots.append(slot);
      });

      clear(pool);
      for (const piece of model.piezas) {
        if (placed.includes(piece.id)) continue;
        const chip = button(piece.label, () => {
          if (locked || placed.length >= model.orden.length) return;
          placed = [...placed, piece.id];
          this.sfx('pop');
          repaint();
        }, { className: 'ae-mg-chip' });
        pool.append(chip);
      }
      if (locked) pool.hidden = true;
    };

    node.append(slots, pool);
    repaint();

    return {
      node,
      read: () => (placed.length === 0 ? null : { kind: 'ordering', ids: placed }),
      lock: () => {
        locked = true;
        repaint();
      },
      destroy: () => undefined
    };
  }

  private matchingWidget(model: Extract<WidgetModel, { kind: 'matching' }>): Widget {
    const pairs: Record<number, number> = {};
    let activeLeft: number | null = null;
    let locked = false;
    const node = el('div', { className: 'ae-mg-match' });
    const leftCol = el('div', { className: 'ae-mg-match-col' });
    const rightCol = el('div', { className: 'ae-mg-match-col' });

    const leftButtons: HTMLButtonElement[] = [];
    const rightButtons: HTMLButtonElement[] = [];

    const ownerOfRight = (rightIndex: number): number | null => {
      for (const [left, right] of Object.entries(pairs)) {
        if (right === rightIndex) return Number(left);
      }
      return null;
    };

    const repaint = (): void => {
      leftButtons.forEach((b, i) => {
        const paired = pairs[i] !== undefined;
        b.className = 'ae-mg-option';
        if (paired) b.classList.add('ae-paired', `ae-link-${i % LINK_COLORS}`);
        if (activeLeft === i) b.classList.add('ae-selected');
      });
      rightButtons.forEach((b, position) => {
        const rightIndex = model.ordenDerecha[position];
        const owner = ownerOfRight(rightIndex);
        b.className = 'ae-mg-option';
        if (owner !== null) b.classList.add('ae-paired', `ae-link-${owner % LINK_COLORS}`);
      });
    };

    model.pares.forEach((pair, i) => {
      const b = el('button', { className: 'ae-mg-option', type: 'button' }, [
        el('span', { className: 'ae-mg-option-text', textContent: pair.izquierda })
      ]);
      b.addEventListener('click', () => {
        if (locked) return;
        if (pairs[i] !== undefined) {
          delete pairs[i];
          activeLeft = null;
        } else {
          activeLeft = i;
        }
        this.sfx('pop');
        repaint();
      });
      leftButtons.push(b);
      leftCol.append(b);
    });

    model.ordenDerecha.forEach((rightIndex) => {
      const b = el('button', { className: 'ae-mg-option', type: 'button' }, [
        el('span', { className: 'ae-mg-option-text', textContent: model.pares[rightIndex].derecha })
      ]);
      b.addEventListener('click', () => {
        if (locked) return;
        if (activeLeft === null) {
          const owner = ownerOfRight(rightIndex);
          if (owner !== null) delete pairs[owner];
          this.sfx('pop');
          repaint();
          return;
        }
        const previousOwner = ownerOfRight(rightIndex);
        if (previousOwner !== null) delete pairs[previousOwner];
        pairs[activeLeft] = rightIndex;
        activeLeft = null;
        this.sfx('moneda');
        repaint();
      });
      rightButtons.push(b);
      rightCol.append(b);
    });

    node.append(leftCol, rightCol);
    repaint();

    return {
      node,
      read: () => (Object.keys(pairs).length === 0 ? null : { kind: 'matching', pairs: { ...pairs } }),
      lock: () => {
        locked = true;
        leftButtons.forEach((b) => (b.disabled = true));
        rightButtons.forEach((b) => (b.disabled = true));
      },
      destroy: () => undefined
    };
  }

  private sortingWidget(model: Extract<WidgetModel, { kind: 'sorting' }>): Widget {
    const assigned: Record<string, string> = {};
    let activePiece: string | null = null;
    let locked = false;
    const node = el('div', { className: 'ae-mg-sort' });
    const pool = el('div', { className: 'ae-mg-pool' });
    const buckets = el('div', { className: 'ae-mg-buckets' });

    const repaint = (): void => {
      clear(pool);
      for (const piece of model.piezas) {
        if (assigned[piece.id] !== undefined) continue;
        const chip = button(piece.label, () => {
          if (locked) return;
          activePiece = activePiece === piece.id ? null : piece.id;
          this.sfx('pop');
          repaint();
        }, { className: activePiece === piece.id ? 'ae-mg-chip ae-selected' : 'ae-mg-chip' });
        pool.append(chip);
      }
      pool.hidden = locked;

      clear(buckets);
      for (const bucket of model.cestas) {
        const box = el('div', { className: 'ae-mg-bucket' }, [
          el('div', { className: 'ae-mg-bucket-label', textContent: bucket.label })
        ]);
        const contents = el('div', { className: 'ae-mg-bucket-items' });
        for (const piece of model.piezas) {
          if (assigned[piece.id] !== bucket.id) continue;
          contents.append(
            button(piece.label, () => {
              if (locked) return;
              delete assigned[piece.id];
              this.sfx('pop');
              repaint();
            }, { className: 'ae-mg-chip ae-in-bucket' })
          );
        }
        box.append(contents);
        box.addEventListener('click', () => {
          if (locked || activePiece === null) return;
          assigned[activePiece] = bucket.id;
          activePiece = null;
          this.sfx('moneda');
          repaint();
        });
        buckets.append(box);
      }
    };

    node.append(pool, buckets);
    repaint();

    return {
      node,
      read: () => (Object.keys(assigned).length === 0 ? null : { kind: 'sorting', buckets: { ...assigned } }),
      lock: () => {
        locked = true;
        repaint();
      },
      destroy: () => undefined
    };
  }

  private wordsearchWidget(model: Extract<WidgetModel, { kind: 'wordsearch' }>): Widget {
    const letras = model.rejilla.map((fila) => [...fila].map((c) => normalizeText(c).toUpperCase()));
    const filas = letras.length;
    const cols = letras.reduce((max, fila) => Math.max(max, fila.length), 0);
    const palabras = model.palabras.map((p) => normalizeText(p).toUpperCase());

    const halladas = new Set<string>();
    const marcas: (number | null)[] = new Array(filas * cols).fill(null);
    let inicio: number | null = null;
    let locked = false;

    const node = el('div', { className: 'ae-mg-ws' });
    const grid = el('div', { className: 'ae-mg-ws-grid' });
    grid.style.setProperty('--ae-ws-cols', String(cols));
    const lado = el('div', { className: 'ae-mg-ws-side' });
    const lista = el('ul', { className: 'ae-mg-ws-list' });
    const estado = el('div', { className: 'ae-mg-ws-status', textContent: '' });

    const celdas: HTMLButtonElement[] = [];
    const items: HTMLLIElement[] = [];

    for (let r = 0; r < filas; r++) {
      for (let c = 0; c < cols; c++) {
        const letra = letras[r][c] ?? '';
        const vacia = letra === '';
        const cell = el('button', {
          className: 'ae-mg-ws-cell',
          type: 'button',
          textContent: letra,
          disabled: vacia,
          attrs: { 'aria-label': vacia ? 'Celda vacía' : `Letra ${letra}, fila ${r + 1}, columna ${c + 1}` }
        });
        const idx = r * cols + c;
        cell.addEventListener('click', () => {
          if (locked || vacia) return;
          if (inicio === null) {
            inicio = idx;
            this.sfx('pop');
          } else if (inicio === idx) {
            inicio = null;
          } else {
            this.sfx('tecleo');
            intentar(inicio, idx);
            inicio = null;
          }
          pintar();
        });
        celdas.push(cell);
        grid.append(cell);
      }
    }

    palabras.forEach((p) => {
      const li = el('li', { className: 'ae-mg-ws-word', textContent: p });
      items.push(li);
      lista.append(li);
    });

    /** Celdas de la recta entre dos índices, o null si no forman línea válida. */
    const linea = (a: number, b: number): number[] | null => {
      const r1 = Math.floor(a / cols);
      const c1 = a % cols;
      const r2 = Math.floor(b / cols);
      const c2 = b % cols;
      const dr = r2 - r1;
      const dc = c2 - c1;
      if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return null;
      const pasos = Math.max(Math.abs(dr), Math.abs(dc));
      const sr = pasos === 0 ? 0 : dr / pasos;
      const sc = pasos === 0 ? 0 : dc / pasos;
      const out: number[] = [];
      for (let k = 0; k <= pasos; k++) out.push((r1 + sr * k) * cols + (c1 + sc * k));
      return out;
    };

    const intentar = (a: number, b: number): void => {
      const camino = linea(a, b);
      if (camino === null) {
        estado.textContent = 'Marca una línea recta: horizontal, vertical o diagonal.';
        estado.className = 'ae-mg-ws-status ae-mg-ws-bad';
        this.sfx('error');
        return;
      }
      const cadena = camino.map((i) => celdas[i].textContent ?? '').join('');
      const inversa = [...cadena].reverse().join('');
      const acierto = palabras.find((p) => !halladas.has(p) && (p === cadena || p === inversa));
      if (acierto === undefined) {
        estado.textContent = `«${cadena}» no está en la lista.`;
        estado.className = 'ae-mg-ws-status ae-mg-ws-bad';
        this.sfx('error');
        return;
      }
      halladas.add(acierto);
      const color = halladas.size - 1;
      camino.forEach((i) => (marcas[i] = color));
      const restantes = palabras.length - halladas.size;
      estado.textContent =
        restantes === 0
          ? `¡Encontraste las ${palabras.length}! Pulsa Comprobar.`
          : `¡«${acierto}»! Quedan ${restantes}.`;
      estado.className = 'ae-mg-ws-status ae-mg-ws-good';
      this.sfx('moneda');
    };

    const pintar = (): void => {
      celdas.forEach((cell, i) => {
        const marca = marcas[i];
        cell.className = 'ae-mg-ws-cell';
        if (marca !== null) cell.classList.add('ae-mg-ws-hit', `ae-mg-ws-c${marca % 6}`);
        if (inicio === i) cell.classList.add('ae-selected');
      });
      palabras.forEach((p, i) => items[i].classList.toggle('ae-done', halladas.has(p)));
    };

    lado.append(el('div', { className: 'ae-mg-ws-side-title', textContent: 'Palabras' }), lista, estado);
    node.append(grid, lado);
    pintar();

    return {
      node,
      read: () => (halladas.size === 0 ? null : { kind: 'wordsearch', words: [...halladas] }),
      lock: () => {
        locked = true;
        celdas.forEach((cell) => (cell.disabled = true));
      },
      destroy: () => undefined
    };
  }

  private buildingWidget(model: Extract<WidgetModel, { kind: 'building' }>): Widget {
    let selected = new Set<string>();
    let locked = false;
    const node = el('div', { className: 'ae-mg-build' });
    const target = el('div', { className: 'ae-mg-target' });
    const pool = el('div', { className: 'ae-mg-pool' });

    const sum = (): number =>
      model.piezas.filter((p) => selected.has(p.id)).reduce((total, p) => total + p.value, 0);

    const repaint = (): void => {
      clear(target);
      const total = sum();
      target.append(
        el('span', { className: 'ae-mg-target-label', textContent: `Objetivo: ${formatNumber(model.objetivo)}` }),
        el('span', {
          className: total === model.objetivo ? 'ae-mg-target-sum ae-hit' : 'ae-mg-target-sum',
          textContent: `Llevas: ${formatNumber(total)}`
        })
      );

      clear(pool);
      for (const piece of model.piezas) {
        const chip = button(`${piece.label} · ${formatNumber(piece.value)}`, () => {
          if (locked) return;
          const next = new Set(selected);
          if (next.has(piece.id)) next.delete(piece.id);
          else next.add(piece.id);
          selected = next;
          this.sfx('pop');
          repaint();
        }, { className: selected.has(piece.id) ? 'ae-mg-chip ae-selected' : 'ae-mg-chip' });
        pool.append(chip);
      }
    };

    node.append(target, pool);
    repaint();

    return {
      node,
      read: () => (selected.size === 0 ? null : { kind: 'building', ids: [...selected] }),
      lock: () => {
        locked = true;
      },
      destroy: () => undefined
    };
  }
}
